import {
  Component, OnInit, forwardRef, Input, ElementRef, ChangeDetectorRef, Output, EventEmitter, Optional,
  AfterViewInit, OnDestroy, HostBinding, OnChanges, SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, AbstractControl, ControlContainer, FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LocaleService } from 'src/app/services/locale-service/locale.service';
import {
  getAriaDescribedBy,
  getAriaInvalidAttr,
} from 'src/app/shared/accessibility/form-control-a11y.mixin';
import {
  formatDateDisplay,
  formatIsoDateOnly,
  formatRawDateDigits,
  parseFlexibleDate,
  toIsoDateOfBirth,
} from './date-format.util';

@Component({
  selector: 'viva-date-input',
  templateUrl: './viva-date-input.component.html',
  styleUrls: ['./viva-date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivaDateInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class VivaDateInputComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() control: AbstractControl | null = null;
  @Input() label?: string;
  @Input() formControlName: string | number | null = null;
  @Input() placeholder?: string;
  @Input() iconStartSrc?: string;
  @Input() iconEndSrc?: string;
  @Input() required?: string;
  @Input() maxlength?: string;
  @Input() minlength?: string;
  @Input() inputmode?: string;
  @Input() autocomplete?: string;
  @Input() onkeydown?: (event: KeyboardEvent) => boolean;
  /** Kept for existing templates; display and parsing always follow {@link usesUSFormat}. */
  @Input() useUSFormat?: boolean;
  @Input() errorNoteId?: string;
  @Input() showCalendarButton = false;
  @Input() calendarAriaLabel?: string;

  @Input() value: any = '';
  @Output() valueChange = new EventEmitter<any>();
  @Output() changeEvent = new EventEmitter<Event>();
  @Output() calendarClick = new EventEmitter<void>();

  @HostBinding('class.has-calendar-button')
  get hasCalendarButton(): boolean {
    return this.showCalendarButton;
  }

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };
  private hostElement: HTMLElement;
  private _dtValue?: string;
  private valueChangeSubscription?: Subscription;
  private langChangeSubscription?: Subscription;
  private lastUSFormat: boolean;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
    private localeService: LocaleService,
    @Optional() private translate?: TranslateService,
  ) {
    this.hostElement = elRef.nativeElement as HTMLElement;
    this.lastUSFormat = this.usesUSFormat;
  }

  /**
   * US = MM/DD/YYYY, else YYYY/MM/DD.
   * Always follows the active culture so the picker updates when the user
   * changes language, even if a parent still passes a stale [useUSFormat].
   */
  get usesUSFormat(): boolean {
    return this.localeService.isUSCulture();
  }

  ngOnInit(): void {
    if (!this.control && this.formControlName !== null && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup;
      if (formGroup) {
        this.control = formGroup.get(String(this.formControlName)) as FormControl;
      }
    }

    if (this.control) {
      this.valueChangeSubscription = this.control.valueChanges.subscribe(() => {
        this.showHideNotch();
      });
    }

    this.lastUSFormat = this.usesUSFormat;
    this.langChangeSubscription = this.translate?.onLangChange.subscribe(() => {
      this.syncFormatFromLocale();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['useUSFormat'] && !changes['useUSFormat'].firstChange) {
      this.syncFormatFromLocale();
    }
  }

  ngAfterViewInit(): void {
    this.showHideNotch();
  }

  ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
    this.langChangeSubscription?.unsubscribe();
  }

  private syncFormatFromLocale(): void {
    const next = this.usesUSFormat;
    this.reformatDisplayedValue(this.lastUSFormat, next);
    this.lastUSFormat = next;
  }

  private reformatDisplayedValue(fromUSFormat: boolean, toUSFormat: boolean): void {
    if (fromUSFormat === toUSFormat) {
      this.cdr.detectChanges();
      this.showHideNotch();
      return;
    }

    const current = this._dtValue || this.value;
    if (typeof current === 'string' && current) {
        const parsed =
          parseFlexibleDate(current, fromUSFormat) ||
          parseFlexibleDate(current, toUSFormat);
        if (parsed) {
          const formatted = formatDateDisplay(parsed, toUSFormat);
        this._dtValue = formatted;
        this.value = formatted;
      }
    }

    this.cdr.detectChanges();
    this.showHideNotch();
  }

  writeValue(value: any): void {
    if (value instanceof Date && !isNaN(value.getTime())) {
      this.value = formatDateDisplay(value, this.usesUSFormat);
      this._dtValue = this.value;
      this.cdr.detectChanges();
      this.showHideNotch();
      return;
    }

    if (typeof value === 'string' && value.trim()) {
      const parsed = parseFlexibleDate(value, this.usesUSFormat);
      this.value = parsed ? formatDateDisplay(parsed, this.usesUSFormat) : value;
    } else {
      this.value = '';
    }

    this._dtValue = this.value;
    this.cdr.detectChanges();
    this.showHideNotch();
  }

  public static parseISODateString(isoString: string): Date {
    if (!isoString) return new Date(NaN); // handle null or empty safely

    // Only strip the time if present
    const datePart = isoString.includes('T') ? isoString.split('T')[0] : isoString;

    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      console.warn('Invalid date format:', isoString);
      return new Date(NaN); // invalid
    }

    const result = new Date(year, month - 1, day); // local date (no TZ shift)
    return result;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.hostElement.setAttribute('disabled', 'true');
    } else {
      this.hostElement.removeAttribute('disabled');
    }
  }


  private formatDebounceTimer?: any;

  onInputChange(event: any): void {
    const inputVal: string = event?.target?.value ?? '';
    const slashCount = (inputVal.match(/\//g) || []).length;
    const shouldDebounce = slashCount >= 2;

    // Store the raw input (formatted or not)
    this._dtValue = inputVal;
    this.value = inputVal;
    this.valueChange.emit(inputVal);

    // Clear any previous debounce timer
    if (this.formatDebounceTimer) {
      clearTimeout(this.formatDebounceTimer);
    }

    if (shouldDebounce) {
      // Debounce reformatting if 2 or more slashes exist (user is editing a nearly complete date)
      this.formatDebounceTimer = setTimeout(() => {
        this.applyFormattedInput(inputVal);
      }, 1500);
    } else {
      // Apply formatting immediately otherwise
      this.applyFormattedInput(inputVal);
    }

    this.changeEvent.emit(event);
    this.cdr.detectChanges();
    this.showHideNotch();
  }

  private applyFormattedInput(rawInput: string): void {
    const formatted = formatRawDateDigits(rawInput, this.usesUSFormat);

    this._dtValue = formatted;
    this.value = formatted;
    this.valueChange.emit(this.value);

    const serverFormat = this.formatForServer();
    if (serverFormat && this.onChange) {
      this.onChange(serverFormat);
    } else {
      console.warn('⚠️ Parsing failed in applyFormattedInput(). Raw:', this._dtValue);
    }

    this.cdr.detectChanges();
  }

  private formatForServer(): string | null {
    return toIsoDateOfBirth(this._dtValue ?? '', this.usesUSFormat);
  }

  onInputBlur(): void {
    this.onTouched();
    this.showHideNotch();
  }

  onCalendarButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.calendarClick.emit();
  }

  isValidDate(dateString?: string): boolean {
    if (this.required === 'true' && !dateString) return false;
    if (!dateString) return true;
    return parseFlexibleDate(dateString, this.usesUSFormat) != null;
  }

  private isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  public static formatRawDigits(rawInput: string, useUSFormat: boolean): string {
    return formatRawDateDigits(rawInput, useUSFormat);
  }

  public static formatDate(date: Date, useUSFormat: boolean): string {
    return formatDateDisplay(date, useUSFormat);
  }

  public static formatISODate(date: Date): string {
    return formatIsoDateOnly(date);
  }

  public static parseDateString(dateStr: string, useUSFormat: boolean): Date | null {
    return parseFlexibleDate(dateStr, useUSFormat);
  }


  get isInvalid(): boolean {
    const isFormatInvalid = !this.isValidDate(this._dtValue);
    return this.control ? (this.control.invalid && (this.control.dirty || this.control.touched)) || isFormatInvalid : isFormatInvalid;
  }

  get ariaInvalid(): 'true' | null {
    return getAriaInvalidAttr(this.isInvalid);
  }

  get ariaDescribedBy(): string | null {
    return getAriaDescribedBy(this.isInvalid, this.errorNoteId);
  }

  get labelWithFormat(): string | undefined {
    if (!this.label)
      return this.label;
    return this.label + this.dateFormat;
  }

  get dateFormat(): string {
    if (this.usesUSFormat)
      return ' (mm/dd/yyyy)';
    else
      return ' (yyyy/mm/dd)';
  }

  get labelText(): string | undefined {
    return this.labelWithFormat && this._dtValue ? this.labelWithFormat : undefined;
  }

  get actualPlaceholder(): string | undefined {
    return (this.placeholder || this.label) + this.dateFormat;
  }

  showHideNotch(): void {
    const notchElement = this.hostElement.querySelector('.input-outline-notch') as HTMLElement | null;
    if (notchElement) {
      const hasValue = this._dtValue != null && this._dtValue !== '';
      notchElement.style.display = hasValue ? 'block' : 'none';
    } else {
      setTimeout(() => this.showHideNotch(), 500);
    }
  }
}
